import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CampaignGroup } from '@/lib/utils/task-inbox-grouping';

interface SimpleTaskItemProps {
  campaignGroup: CampaignGroup;
  isSelected: boolean;
  onSelect: () => void;
}

export function SimpleTaskItem({ campaignGroup, isSelected, onSelect }: SimpleTaskItemProps) {
  /**
   * Generate a human-readable action summary with accurate keyword counts
   *
   * This function prioritizes showing the uniqueKeywordCount (number of unique
   * keywords counted by ID) from the task-inbox-grouping.ts logic.
   *
   * If that's not available, it uses a cascading fallback approach similar
   * to the one in task-inbox-grouping.ts to ensure we always show a reasonable
   * count in the UI.
   */
  const generateActionSummary = (): string => {
    // Multi-tier approach to get the most accurate keyword count
    let count = 0;

    // First choice: Use uniqueKeywordCount if available and non-zero
    if (campaignGroup.uniqueKeywordCount && campaignGroup.uniqueKeywordCount > 0) {
      count = campaignGroup.uniqueKeywordCount;
    }
    // Second choice: Use keywords array length if available
    else if (campaignGroup.keywords && campaignGroup.keywords.length > 0) {
      count = campaignGroup.keywords.length;
    }
    // Third choice: Calculate from adActions if available
    else if (campaignGroup.adActions && campaignGroup.adActions.length > 0) {
      count = campaignGroup.adActions.reduce((sum, action) => sum + (action.count || 0), 0);
    }
    // Last resort: Fall back to taskCount
    else {
      count = campaignGroup.taskCount || 0;
    }

    // Normalize action type for consistent comparison (handle both cases)
    const actionType = (campaignGroup.actionType || '').toLowerCase();

    // Check for budget action based on either the action type or hasBudgetAction flag
    if (actionType.includes('set_budget') || campaignGroup.hasBudgetAction) {
      // If we have both before and after values, show a specific message
      if (campaignGroup.budgetBeforeValue !== undefined && campaignGroup.budgetAfterValue !== undefined) {
        // Format budget values to 2 decimal places
        const beforeValue = Number(campaignGroup.budgetBeforeValue).toFixed(2);
        const afterValue = Number(campaignGroup.budgetAfterValue).toFixed(2);
        return `Updated budget from $${beforeValue} to $${afterValue}`;
      }
      // Generic fallback message
      return 'Updated budget';
    }

    // Use includes() for more flexible matching for keyword-related actions
    if (actionType.includes('delete_keyword')) {
      return `Removed ${count} ${count === 1 ? 'keyword' : 'keywords'}`;
    } else if (actionType.includes('add_keyword') && !actionType.includes('negative')) {
      return `Added ${count} ${count === 1 ? 'keyword' : 'keywords'}`;
    } else if (actionType.includes('pause_keyword')) {
      return `Paused ${count} ${count === 1 ? 'keyword' : 'keywords'}`;
    } else if (
      actionType.includes('add_negative_keyword') ||
      (actionType.includes('add_keyword') && actionType.includes('negative'))
    ) {
      return `Added ${count} negative ${count === 1 ? 'keyword' : 'keywords'}`;
    } else if (actionType.includes('add_exact_match_keyword')) {
      return `Added ${count} exact match ${count === 1 ? 'keyword' : 'keywords'}`;
    } else if (actionType.includes('budget') || campaignGroup.hasBudgetAction) {
      // Extra safety check for budget-related actions
      return 'Updated budget';
    } else {
      // For all other action types
      const actionName = actionType
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

      return actionName || `Campaign modification`;
    }
  };

  const actionSummary = generateActionSummary();
  const campaignName = campaignGroup.campaignName || `Campaign #${campaignGroup.campaignId}`;

  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formattedDate = formatDate(campaignGroup.createdAt);

  return (
    <Card
      className={`border-0 rounded-none hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-[#290BD9]/10' : ''
      } cursor-pointer`}
      onClick={onSelect}
    >
      <div className='flex items-center px-4 py-3'>
        <Checkbox checked={isSelected} className='mr-3' onCheckedChange={() => onSelect()} />
        <div className='flex-1'>
          <div className='font-medium'>
            {campaignGroup.isUndone ? 'Reverted: ' : ''}
            {actionSummary}
          </div>
          <div className='text-sm text-muted-foreground'>
            {campaignGroup.isUndone ? '' : 'From '}
            {campaignName}
          </div>
          <div className='text-sm text-muted-foreground mt-1'>{formattedDate}</div>
        </div>
      </div>
    </Card>
  );
}

export default SimpleTaskItem;
